// UTILS
import { ref } from "vue"
import { defineStore, storeToRefs } from "pinia"

// TYPES
import type { Answers } from "@/features/voice/store/voice.types"

// STORES
import { useUserStore } from "@/stores/user"
import { useConfigStore } from "@/stores/config"
import { initAnswers } from "@/features/voice/store/voice.data"

// API
import { restartVoice } from "@/api/viktor/assessment/assessment"

export const useAnswersStore = defineStore("voiceAnswersStore", () => {
  const configStore = useConfigStore()
  const userStore = useUserStore()
  const { getUserUuid, getVoiceUuid } = storeToRefs(userStore)
  const { nextAssessment, getVoiceByUuid } = storeToRefs(userStore)

  const answers = ref<Answers>(initAnswers)

  const selectedMicrophoneId = ref<string>(
    localStorage.getItem("selectedMicrophoneId") || ""
  )
  const selectedSpeakerId = ref<string>(
    localStorage.getItem("selectedSpeakerId") || ""
  )

  // GETTERS

  /**
   * Fetch voice assessment
   * @returns Voice assessment data
   */
  async function fetchVoice() {
    const nextAssessmentName = nextAssessment.value?.next_assessment?.assessment
    // Si pas de voice de prévu, on retourne au stepper
    if (nextAssessmentName !== "voice") {
      document.location.href = configStore.urls.app
    }

    return getVoiceByUuid.value(getVoiceUuid.value)
  }

  // ACTIONS

  /**
   * Set microphone ID
   * @param id Microphone ID
   */
  const setMicrophoneId = (id: string) => {
    selectedMicrophoneId.value = id
    localStorage.setItem("selectedMicrophoneId", id)
  }

  /**
   * Set speaker ID
   * @param id Speaker ID
   */
  const setSpeakerId = (id: string) => {
    selectedSpeakerId.value = id
    localStorage.setItem("selectedSpeakerId", id)
  }

  /**
   * Retake test
   */
  async function retakeTest() {
    await restartVoice(getUserUuid.value, getVoiceUuid.value)
    await userStore.fetchWorkflows()
    await userStore.fetchAssessmentSummary()
  }

  return {
    answers,
    fetchVoice,
    retakeTest,
    selectedMicrophoneId,
    selectedSpeakerId,
    setMicrophoneId,
    setSpeakerId,
  }
})
